"use client";
import { useState, useEffect, useRef, useCallback, useSyncExternalStore, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { XMarkIcon } from "@heroicons/react/24/solid";
import PhoneInput from "react-phone-input-2";
import { parsePhoneNumber } from "libphonenumber-js";
import { useTranslations } from "next-intl";
import { logEvent } from "@/app/[locale]/googleAnalytics";

// Клиентский mount-guard для портала без setState-в-эффекте.
const emptySubscribe = () => () => {};
function useMounted(): boolean {
  return useSyncExternalStore(emptySubscribe, () => true, () => false);
}

const isValidPhone = (phone: string): boolean => {
  try {
    return parsePhoneNumber(phone).isValid();
  } catch {
    return false;
  }
};

type Props = {
  // Как отрисовать кнопку-триггер.
  // header — компактная кнопка для шапки (одинаковая форма с «Арендой», покупка залита
  // ярким акцентом и доминирует); menu — крупная кнопка во всю ширину для бургера
  // (одинаковая с арендной в бургере).
  variant?: "header" | "menu";
  className?: string;
};

/**
 * Кнопка «Купить квартиру» + модалка заявки на ПОКУПКУ (Имя + Телефон +
 * необязательный тип юнита + комментарий). Заявка уходит в Telegram через
 * /api/contact в ОТДЕЛЬНЫЙ канал продаж (аренда идёт в свой канал). Прайс-лист
 * больше не отдаётся автоматически — менеджер связывается и присылает актуальный.
 */
export default function PurchaseModal({ variant = "header", className = "" }: Props) {
  const t = useTranslations("purchase");
  const tMenu = useTranslations("menu");

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [unit, setUnit] = useState("");
  const [comment, setComment] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [errors, setErrors] = useState<{ name?: string; phone?: string }>({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState("");

  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const mounted = useMounted();

  // Стиль react-phone-input-2 подгружаем по URL на маунте (не при open), чтобы к
  // открытию модалки CSS уже был готов — тот же приём, что в rent-modal.
  useEffect(() => {
    const HREF = "/phone-input.css";
    if (document.querySelector(`link[href="${HREF}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = HREF;
    document.head.appendChild(link);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setTimeout(() => {
      setDone(false);
      setServerError("");
      setErrors({});
      setName("");
      setPhone("");
      setUnit("");
      setComment("");
    }, 300);
  }, []);

  // focus-trap + Esc + возврат фокуса (a11y managed-focus), как в rent-modal.
  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    const focusables = () =>
      dialog
        ? Array.from(
            dialog.querySelectorAll<HTMLElement>(
              'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
            ),
          ).filter((el) => el.offsetParent !== null)
        : [];
    focusables()[0]?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== "Tab") return;
      const els = focusables();
      if (els.length === 0) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    const trigger = triggerRef.current;
    return () => {
      document.removeEventListener("keydown", onKey);
      trigger?.focus();
    };
  }, [open, close]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setServerError("");

    const formattedPhone = phone.startsWith("+") ? phone : `+${phone}`;
    const nextErrors: { name?: string; phone?: string } = {};
    if (!name.trim()) nextErrors.name = t("errorName");
    if (!isValidPhone(formattedPhone)) nextErrors.phone = t("errorPhone");
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setLoading(true);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          phone: formattedPhone,
          apartmentType: unit,
          comment: comment.trim(),
          website,
        }),
      });
      if (!res.ok) throw new Error("request failed");
      setDone(true);
      logEvent("Заявка на покупку", {
        event_category: "Purchase Form",
        event_label: "Header buy request",
      });
    } catch {
      setServerError(t("error"));
    } finally {
      setLoading(false);
    }
  };

  // Кнопка «Купить» — главный CTA, залита ярким акцентом (доминирует над «Арендой»).
  // header — форма как у старой «Contact us» (округлая с ОДНОЙ стороны + плоский язычок
  //   с другой), фикс-высота h-[46px], БЕЗ переноса (whitespace-nowrap). «Купить»
  //   округлена СЛЕВА (rounded-l-full), язычок справа — зеркало «Аренды».
  // menu — крупная во всю ширину для бургера.
  const triggerClass =
    variant === "menu"
      ? "w-full bg-[#04BBDC] hover:bg-[#03A6C4] text-[#012a33] font-semibold px-4 py-[14px] rounded-full text-sm text-center"
      : "inline-flex items-center justify-center h-[46px] whitespace-nowrap bg-[#04BBDC] hover:bg-[#03A6C4] text-[#012a33] text-sm font-semibold px-6 rounded-l-full rounded-r-md";

  // В шапке — короткий лейбл «Купить»/«Buy» (menu.buyButton), не длинный заголовок
  // модалки purchase.cta («Купить квартиру»), иначе текст переносится и кнопка раздувается.
  const label = variant === "menu" ? t("cta") : tMenu("buyButton");

  const units = [t("unitStudio"), t("unit1"), t("unit2")];

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        className={`${triggerClass} ${className} transition-colors`}
      >
        {label}
      </button>

      {mounted &&
        createPortal(
          <AnimatePresence>
            {open && (
              <motion.div
                className="fixed inset-0 z-[100] flex items-center justify-center p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <button
                  type="button"
                  aria-label={t("close")}
                  onClick={close}
                  className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                />

                <motion.div
                  ref={dialogRef}
                  role="dialog"
                  aria-modal="true"
                  aria-label={t("title")}
                  className="relative w-full max-w-[420px] max-h-[calc(100dvh-2rem)] overflow-y-auto bg-white rounded-2xl p-6 shadow-xl"
                  initial={{ opacity: 0, y: 24, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 24, scale: 0.98 }}
                  transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                >
                  <button
                    type="button"
                    onClick={close}
                    aria-label={t("close")}
                    className="absolute top-4 right-4 text-[#005F70] hover:text-[#04BBDC] transition-colors"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>

                  {done ? (
                    <div className="py-8 text-center">
                      <p className="text-[#005F70] font-semibold text-lg">{t("success")}</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                      <div>
                        <h2 className="text-[22px] font-bold text-[#005F70]">{t("title")}</h2>
                        <p className="text-sm text-gray-600 mt-1">{t("subtitle")}</p>
                      </div>

                      <div>
                        <input
                          type="text"
                          name="name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder={t("name")}
                          autoComplete="name"
                          className="w-full h-12 px-4 border border-gray-300 rounded-md focus:outline-none focus:border-[#04BBDC]"
                        />
                        {errors.name && <span className="text-red-500 text-xs mt-1 block">{errors.name}</span>}
                      </div>

                      <div className="w-full min-w-0">
                        <PhoneInput
                          country={"th"}
                          value={phone}
                          onChange={setPhone}
                          inputProps={{ name: "phone", autoComplete: "tel" }}
                          containerClass="!w-full"
                          inputClass="!w-full !h-12"
                        />
                        {errors.phone && <span className="text-red-500 text-xs mt-1 block">{errors.phone}</span>}
                      </div>

                      {/* Необязательный тип юнита — квалифицирует лид без трения
                          (3 понятных варианта вместо 28 внутренних кодов). */}
                      <div>
                        <select
                          name="apartmentType"
                          autoComplete="off"
                          value={unit}
                          onChange={(e) => setUnit(e.target.value)}
                          className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:border-[#04BBDC]"
                        >
                          <option value="">{t("unitPlaceholder")}</option>
                          {units.map((u) => (
                            <option key={u} value={u}>
                              {u}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <textarea
                          name="comment"
                          autoComplete="off"
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          placeholder={t("comment")}
                          rows={3}
                          maxLength={1000}
                          className="w-full px-4 py-3 border border-gray-300 rounded-md resize-none focus:outline-none focus:border-[#04BBDC]"
                        />
                      </div>

                      {/* honeypot */}
                      <input
                        type="text"
                        name="website"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        className="hidden"
                      />

                      {serverError && <span className="text-red-500 text-sm">{serverError}</span>}

                      <button
                        type="submit"
                        disabled={loading}
                        className="h-12 bg-[#04BBDC] hover:bg-[#03A6C4] disabled:opacity-60 transition-colors text-[#012a33] font-medium rounded-md"
                      >
                        {loading ? t("submitting") : t("submit")}
                      </button>
                    </form>
                  )}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </>
  );
}
